/**
 * Platform auth adapter: native (Capacitor) uses @capacitor-firebase/authentication,
 * web/PWA uses existing Firebase JS SDK. Same sign-in options; no PII or tokens logged.
 */

import type { AuthCredential, ConfirmationResult } from 'firebase/auth';
import { Capacitor } from '@capacitor/core';
import { isNativeApp, getIOSSimulatorAppleSignInHint } from '../platform';
import { auth, googleProvider } from '../firebase';
import {
  signInWithCredential,
  signInWithPopup,
  signInWithRedirect,
  signOut as firebaseSignOut,
  GoogleAuthProvider,
  PhoneAuthProvider,
  OAuthProvider,
} from 'firebase/auth';
import {
  initRecaptchaVerifier,
  clearRecaptchaVerifier,
  isPopupBlocked,
} from '../firebase';

/** Result of starting phone sign-in: either verificationId (native) or confirmationResult (web). */
export type PhoneSignInStartResult =
  | { verificationId: string }
  | { confirmationResult: ConfirmationResult };

/**
 * Sign in with Google. On native uses plugin and signs in web layer with credential; on web uses popup/redirect.
 */
export async function signInWithGoogle(): Promise<void> {
  if (isNativeApp() && Capacitor.isNativePlatform()) {
    const { FirebaseAuthentication } = await import('@capacitor-firebase/authentication');
    const result = await FirebaseAuthentication.signInWithGoogle();
    const cred = result.credential;
    if (!cred?.idToken) {
      throw new Error('Native Google sign-in failed: missing credential');
    }
    const credential = GoogleAuthProvider.credential(cred.idToken, cred.accessToken ?? undefined);
    await signInWithCredential(auth, credential);
    return;
  }

  try {
    await signInWithPopup(auth, googleProvider);
  } catch (error) {
    if (isPopupBlocked(error)) {
      await signInWithRedirect(auth, googleProvider);
      return;
    }
    throw error;
  }
}

/**
 * Sign in with Apple. Only available on iOS native builds.
 * Uses @capacitor-firebase/authentication plugin for native Sign in with Apple.
 * Required for App Store compliance (Guideline 4.8) when offering Google Sign-In.
 *
 * IMPORTANT: Per Capacitor Firebase docs, Apple Sign-In with Firebase JS SDK
 * requires skipNativeAuth: true. The plugin gets credentials from native layer,
 * then we sign in on the web layer using signInWithCredential.
 */
export async function signInWithApple(): Promise<void> {
  if (!isNativeApp() || !Capacitor.isNativePlatform()) {
    throw new Error('Sign in with Apple is only available on iOS');
  }

  const { FirebaseAuthentication } = await import('@capacitor-firebase/authentication');

  try {
    // skipNativeAuth: true is REQUIRED for Apple Sign-In with Firebase JS SDK
    // This gets credentials from native layer without signing in natively
    // Type assertion needed due to dynamic import inference limitations
    const result = await (FirebaseAuthentication.signInWithApple as (
      options?: { skipNativeAuth?: boolean }
    ) => Promise<{ credential?: { idToken?: string; nonce?: string; accessToken?: string } }>)({
      skipNativeAuth: true,
    });

    const cred = result.credential;

    if (!cred?.idToken) {
      throw new Error('Apple sign-in failed: missing idToken from native credential');
    }

    // Create OAuthCredential for Apple provider with idToken and rawNonce
    const provider = new OAuthProvider('apple.com');
    const credential = provider.credential({
      idToken: cred.idToken,
      rawNonce: cred.nonce,
    });

    await signInWithCredential(auth, credential);
  } catch (error: unknown) {
    // Check for simulator-specific hint in dev only
    if (import.meta.env.DEV) {
      const simulatorHint = getIOSSimulatorAppleSignInHint();
      if (simulatorHint) {
        console.warn('[AppleAuth] Simulator hint:', simulatorHint);
      }
    }

    throw error;
  }
}

/**
 * Check if Sign in with Apple is available (iOS native only)
 */
export function isAppleSignInAvailable(): boolean {
  return isNativeApp() && Capacitor.isNativePlatform();
}

/**
 * Start phone sign-in. On native: plugin sends SMS and returns verificationId. On web: reCAPTCHA + send code, returns confirmationResult.
 */
export async function signInWithPhone(phoneNumber: string): Promise<PhoneSignInStartResult> {
  if (isNativeApp() && Capacitor.isNativePlatform()) {
    const { FirebaseAuthentication } = await import('@capacitor-firebase/authentication');
    return new Promise<{ verificationId: string }>((resolve, reject) => {
      const cleanup = () => { FirebaseAuthentication.removeAllListeners().catch(() => {}); };

      const handleCodeSent = (event: unknown) => {
        cleanup();
        const e = event as { verificationId: string };
        resolve({ verificationId: e.verificationId });
      };

      const handleFailed = (event: unknown) => {
        cleanup();
        const e = event as { message?: string; code?: string };
        const errorMessage = e.message || 'Phone verification failed';
        reject(new Error(errorMessage));
      };

      void Promise.all([
        FirebaseAuthentication.addListener('phoneCodeSent', handleCodeSent),
        FirebaseAuthentication.addListener('phoneVerificationFailed', handleFailed),
      ]).then(() => {
        FirebaseAuthentication.signInWithPhoneNumber({ phoneNumber }).catch((err: unknown) => {
          cleanup();
          reject(err);
        });
      }, (err) => {
        cleanup();
        reject(err);
      });
    });
  }

  clearRecaptchaVerifier();
  const verifier = initRecaptchaVerifier();
  const { signInWithPhoneNumber } = await import('firebase/auth');
  const confirmationResult = await signInWithPhoneNumber(auth, phoneNumber, verifier);
  return { confirmationResult };
}

/**
 * Complete phone sign-in with verification code. Use after signInWithPhone returned verificationId (native) or store uses confirmationResult.confirm (web).
 */
export async function confirmPhoneWithCode(
  verificationId: string,
  code: string
): Promise<AuthCredential> {
  return PhoneAuthProvider.credential(verificationId, code);
}

/**
 * Sign out. On native also signs out from plugin so native and web stay in sync.
 */
export async function signOut(): Promise<void> {
  if (isNativeApp() && Capacitor.isNativePlatform()) {
    try {
      const { FirebaseAuthentication } = await import('@capacitor-firebase/authentication');
      await FirebaseAuthentication.signOut();
    } catch {
      // Plugin sign-out best-effort; continue to clear web layer
    }
  }
  clearRecaptchaVerifier();
  await firebaseSignOut(auth);
}
